import 'react-day-picker/lib/style.css';

import { useCallback, useState } from 'react';

import Card from '@/components/Card';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import calendarService from '@/services/calendar.service';
import moment from 'moment-timezone';

const DAYS_OF_WEEK = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const mm = String(min).padStart(2, '0');
      const hh = String(hour).padStart(2, '0');
      options.push({
        label: `${displayHour}:${mm} ${ampm}`,
        value: `${hh}:${mm}`,
      });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const friendlyTimezone = (tz) => {
  if (!tz) return '';
  const abbr = moment.tz(tz).format('z');
  const city = tz.split('/').pop().replace(/_/g, ' ');
  return `${abbr} (${city})`;
};

const nextDefaultStart = (lastEnd) => {
  const idx = TIME_OPTIONS.findIndex((t) => t.value === lastEnd);
  const bumpedIdx = Math.min(idx + 4, TIME_OPTIONS.length - 5);
  return TIME_OPTIONS[bumpedIdx >= 0 ? bumpedIdx : 0].value;
};

const nextDefaultEnd = (start) => {
  const idx = TIME_OPTIONS.findIndex((t) => t.value === start);
  const endIdx = Math.min(idx + 8, TIME_OPTIONS.length - 1);
  return TIME_OPTIONS[endIdx].value;
};

const RecurringAvailability = ({
  providerId,
  platformId,
  timezone,
  onComplete,
}) => {
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([
    { start: '09:00', end: '12:00' },
  ]);
  const [rangeStart, setRangeStart] = useState(undefined);
  const [rangeEnd, setRangeEnd] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const toggleDay = (dayValue) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue],
    );
  };

  const updateTimeBlock = (index, field, value) => {
    setTimeBlocks((prev) =>
      prev.map((block, i) =>
        i === index ? { ...block, [field]: value } : block,
      ),
    );
  };

  const addTimeBlock = () => {
    const lastBlock = timeBlocks[timeBlocks.length - 1];
    const newStart = nextDefaultStart(lastBlock.end);
    const newEnd = nextDefaultEnd(newStart);
    setTimeBlocks([...timeBlocks, { start: newStart, end: newEnd }]);
  };

  const removeTimeBlock = (index) => {
    setTimeBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const allBlocksValid = timeBlocks.every((block) => block.start < block.end);

  const getMatchingDates = useCallback(() => {
    if (!rangeStart || !rangeEnd || selectedDays.length === 0) return [];
    const dates = [];
    const current = moment(rangeStart).startOf('day');
    const end = moment(rangeEnd).startOf('day');
    while (current.isSameOrBefore(end)) {
      if (selectedDays.includes(current.day())) {
        dates.push(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }
    return dates;
  }, [rangeStart, rangeEnd, selectedDays]);

  const handleCreate = async () => {
    const dates = getMatchingDates();
    if (dates.length === 0) {
      setMessage({
        type: 'error',
        text: 'No matching dates found. Please check your selections.',
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    const totalBlocks = dates.length * timeBlocks.length;
    let created = 0;
    try {
      for (const date of dates) {
        for (const block of timeBlocks) {
          const startISO = moment
            .tz(`${date} ${block.start}`, 'YYYY-MM-DD HH:mm', timezone)
            .toISOString();
          const endISO = moment
            .tz(`${date} ${block.end}`, 'YYYY-MM-DD HH:mm', timezone)
            .toISOString();
          await calendarService.setAvailability(
            providerId,
            platformId,
            startISO,
            endISO,
          );
          created++;
        }
      }
      const plural = created !== 1 ? 's' : '';
      setMessage({
        type: 'success',
        text: `Created ${created} availability block${plural} successfully.`,
      });
      onComplete();
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Created ${created} of ${totalBlocks} blocks. An error occurred.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    selectedDays.length > 0 &&
    rangeStart &&
    rangeEnd &&
    allBlocksValid &&
    timeBlocks.length > 0;
  const dateCount = getMatchingDates().length;
  const totalCount = dateCount * timeBlocks.length;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-bold text-lg">Recurring Availability</div>
          {timezone && (
            <span className="text-sm text-gray-500">
              Times shown in {friendlyTimezone(timezone)}
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Days of Week
          </label>
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => toggleDay(value)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  selectedDays.includes(value)
                    ? 'bg-mmj-blue text-white border-mmj-blue'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-mmj-blue'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Time Blocks
          </label>
          {timeBlocks.map((block, index) => (
            <div key={index} className="flex items-end space-x-3">
              <div className="flex-1">
                {index === 0 && (
                  <label className="block text-xs text-gray-500 mb-1">
                    Start
                  </label>
                )}
                <select
                  value={block.start}
                  onChange={(e) =>
                    updateTimeBlock(index, 'start', e.target.value)
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm"
                >
                  {TIME_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                {index === 0 && (
                  <label className="block text-xs text-gray-500 mb-1">
                    End
                  </label>
                )}
                <select
                  value={block.end}
                  onChange={(e) =>
                    updateTimeBlock(index, 'end', e.target.value)
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm"
                >
                  {TIME_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-shrink-0" style={{ width: 36 }}>
                {timeBlocks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTimeBlock(index)}
                    className="h-9 w-9 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Remove time block"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          {!allBlocksValid && (
            <p className="text-sm text-red-500">
              Each block&#39;s end time must be after its start time.
            </p>
          )}
          <button
            type="button"
            onClick={addTimeBlock}
            className="text-sm font-medium text-mmj-blue hover:underline"
          >
            + Add Time Block
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <DayPickerInput
              value={rangeStart}
              onDayChange={(day) => setRangeStart(day)}
              placeholder="Select start date"
              dayPickerProps={{
                selectedDays: rangeStart,
                disabledDays: rangeEnd ? { after: rangeEnd } : undefined,
              }}
              inputProps={{
                className:
                  'block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm',
                readOnly: true,
              }}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <DayPickerInput
              value={rangeEnd}
              onDayChange={(day) => setRangeEnd(day)}
              placeholder="Select end date"
              dayPickerProps={{
                selectedDays: rangeEnd,
                disabledDays: rangeStart ? { before: rangeStart } : undefined,
              }}
              inputProps={{
                className:
                  'block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm',
                readOnly: true,
              }}
            />
          </div>
        </div>

        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            {dateCount} {dateCount !== 1 ? 'days' : 'day'} x {timeBlocks.length}{' '}
            {timeBlocks.length !== 1 ? 'blocks' : 'block'} = {totalCount} total
          </p>
        )}

        {!isValid && !loading && (
          <p className="text-sm text-gray-400">
            {selectedDays.length === 0 ? 'Select at least one day. ' : ''}
            {!rangeStart ? 'Pick a start date. ' : ''}
            {!rangeEnd ? 'Pick an end date. ' : ''}
            {!allBlocksValid ? 'Fix time block errors above. ' : ''}
          </p>
        )}

        {message && (
          <div
            className={`text-sm p-3 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <button
          type="button"
          onClick={handleCreate}
          disabled={!isValid || loading}
          className={`px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
            isValid && !loading
              ? 'bg-mmj-blue hover:opacity-90'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {loading ? 'Creating...' : 'Create Schedule'}
        </button>
      </div>
    </Card>
  );
};

export default RecurringAvailability;
